import { type IFeatureFlags } from '@audacy-clients/core/atoms/config/settings';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import URI from 'urijs';
import { assign } from 'xstate';
import { choose, sendParent } from 'xstate/lib/actions';
import AudacyLogger from '../../../AudacyLogger';
import clientServicesConfig, { AdvertisingIdAttsType } from '../../../Config';
import {
  BundleID,
  EntityType,
  Platform,
  PlayerAction,
  PlayerMetric,
  SourceStringsMap,
  StationSubType,
  StoreID,
  StoreURL,
} from '../../../Constants';
import {
  type ICredentialsProvider,
  type ILocationProvider,
  type IMarketingDataProvider,
} from '../../../Container';
import DataServices from '../../../dataServices/DataServices';
import type Episode from '../../../dataServices/Episode';
import type StandaloneChapter from '../../../dataServices/StandaloneChapter';
import Station from '../../../dataServices/Station';
import { type ILogger } from '../../../logger';
import { sha256 } from '../../../Utilities';
import {
  webSafe64,
  serializeDOMException,
  featureFlagMap,
  isEnableTraceSessionEnabled,
} from '../../../utils';
import { getGppString } from '../../../utils/gpp-util';
import { getRubiconUserId, syncMagnite } from '../../../utils/magnite';
import syncCookies from '../../../utils/syncCookies';
import {
  EConvivaContentType,
  type IPlayer,
  type TContentInfoStatic,
  type IPlayerMetadata,
} from '../../players/types';
import { type TPlayableObject, type IFailure } from '../../types';
import {
  AD_CONTENT_TYPES,
  AMPERWAVE_URL_TEST,
  INITIAL_RETRY_DELAY,
  MAX_DELAY,
  MAX_RETRY_ATTEMPTS,
} from '../constants';
import { type ILiveStationAudioSource, type IStreamerMachineContext, type ITime } from '../types';
import { appendAmperwaveLocationParams } from './amperwave/location';


// eslint-disable-next-line @typescript-eslint/no-explicit-any
const autoplayAction: any = choose([
  {
    cond: ({ autoplay }: IStreamerMachineContext<TPlayableObject>) => autoplay,
    actions: [
      sendParent(
        (ctx, _) => ({
          type: 'PLAY',
          autoplay: ctx.autoplay
        })
      )
    ],
  },
  {
    actions: [
      // Destroy the stream to stop infinite HLS buffering
      sendParent({
        type: 'PAUSE',
      }),
      // Reset autoplay to true (the default)
      assign(() => ({ autoplay: true })),
    ],
  },
]);

export const DEFAULT_PLAYER_ACTIONS = {
  play: ({ player }: IStreamerMachineContext<TPlayableObject>) => player?.play(), //todo: maybe we should move it to specific machine
  pause: ({ player }: IStreamerMachineContext<TPlayableObject>) => player.pause(),
  stop: ({ player }: IStreamerMachineContext<TPlayableObject>) => player.stop(),
  autoplay: autoplayAction,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logPlaybackError: (
    { contentType, item, logger, dataProvider }: IStreamerMachineContext<TPlayableObject>,
    event: object | undefined,
  ) => {
    const { error, errorMessage } = (event as IFailure) || {};
    const extractedError = error ? error.raw || error : errorMessage;
    const message = `Playback error for ${contentType} from ${item.getId()} - ${item.getTitle()}`;

    const isBrowser = typeof document !== 'undefined';
    let status = 'online';
    if (isBrowser && !window.navigator.onLine) {
      status = 'offline';
    }

    const networkStatus = isBrowser ? ` Network Status: ${status}` : '';

    const apiStats = JSON.stringify(dataProvider.stats);

    const isDOMException =
      typeof DOMException !== 'undefined' && extractedError instanceof DOMException;

    const errorString = isDOMException
      ? serializeDOMException(extractedError)
      : JSON.stringify(extractedError);

    const extractedErrorMessage = `Error: ${errorString} Api Stats: ${apiStats} ${networkStatus}`;
    logger?.error(`${message}: ${extractedErrorMessage}`);
  },

  sendPlaybackErrorEvent: (
    context: IStreamerMachineContext<TPlayableObject>,
    event: object | undefined,
  ) => {
    context.analyticsProvider.sendPlaybackErrorEvent(context, event);
  },

  logPlaybackFailure: async ({
    contentType,
    item,
    logger,
    errors,
    firstLoadTime,
    analyticsProvider,
    retryCount,
    dataProvider,
    deviceInfoProvider,
  }: IStreamerMachineContext<TPlayableObject>) => {
    const isBrowser = typeof document !== 'undefined';
    let status = 'online';
    if (isBrowser && !window.navigator.onLine) {
      status = 'offline';
    }

    const networkStatus = isBrowser ? ` Network Status: ${status}` : '';
    const apiStats = JSON.stringify(dataProvider.stats);

    const { bandwidthEstimateBPS, historicBandwidthEstimateBPS, type } =
      (await deviceInfoProvider?.getNetworkInfo()) || {};

    const message = `Playback failed for ${item.getId()} - ${item.getTitle()} ${
      contentType ? `- ${contentType} Api Stats: ${apiStats} ${networkStatus}` : ''
    }`;
    logger?.error(message);

    analyticsProvider.sendEventToListener({
      type: PlayerMetric.STREAM_PLAYBACK_FAILURE,
      eventDetails: {
        stationId: item.getId(),
        streamerType: contentType,
        uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
        mostRecentError: errors?.[0],
        retryCount,
        bandwidthEstimateBPS,
        historicBandwidthEstimateBPS,
        connectivityType: type,
      },
    });
  },
  logLoadingError: ({ item, logger }: IStreamerMachineContext<TPlayableObject>) => {
    const message = `Failed to load sources from ${item.getId()} - ${item.getTitle()}`;
    logger?.error(message);
  },
  logRetry: ({
    item,
    retryCount = 0,
    errors,
    firstLoadTime,
    analyticsProvider,
    contentType,
  }: IStreamerMachineContext<TPlayableObject>) => {
    if (retryCount < MAX_RETRY_ATTEMPTS) {
      //const message = `Playback Failed for ${item.getId()} - ${item.getTitle()}. Attempt ${retryCount} of ${MAX_RETRY_ATTEMPTS} to restart playback.`;
      //TODO: send message error to future logging service
      AudacyLogger.log(
        `[StationMachine] StreamerUtils: Retry Attempt ${retryCount} for ${item.getId()}`,
      );
    }
    analyticsProvider.sendEventToListener({
      type: PlayerMetric.STREAM_RECONNECT_ATTEMPT,
      eventDetails: {
        stationId: item.getId(),
        streamerType: contentType,
        uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
        mostRecentError: errors?.[0],
        retryCount,
      },
    });
  },
  // Analytics Failed Playback
  sendPlaybackFailedEvent: ({
    analyticsProvider,
    item,
    deviceInfoProvider,
  }: IStreamerMachineContext<TPlayableObject>) => {
    const contentId = item.getId();

    const audioRoute = deviceInfoProvider?.getAudioRoute();

    if (contentId) {
      analyticsProvider.sendPlayerEvent({
        type: PlayerAction.PLAYBACK_FAILED,
        contentId: contentId,
        streamUrl: getFirstAudioSourceUrl(item?.data.streamUrl),
        connectionType: audioRoute,
      });
    }
  },
};

type IAmperwaveOptions = {
  platform: Platform;
  marketingDataProvider?: IMarketingDataProvider;
  credentialsProvider?: ICredentialsProvider;
  locationProvider?: ILocationProvider;
  item: TPlayableObject;
  sessionId?: string;
  featureFlags?: IFeatureFlags;
};

type IAudioPreroll = {
  companionAd: string;
  creativeUrl: string;
  adStartTrackers: Array<string>;
  companionAdClickThroughTracker: string;
};

export const isContentTypeAd = (contentType?: string): boolean =>
  !!contentType && AD_CONTENT_TYPES.has(contentType);

const shouldOptimizeAdBreaks = (
  featureFlags: IFeatureFlags | undefined,
  item: TPlayableObject,
): boolean => {
  return (
    !!featureFlags?.optimizedAdBreaks &&
    item.data.entityType === EntityType.STATION &&
    item.data.entitySubtype === StationSubType.BROADCAST &&
    item.data.ownerOperatorIdent === '501-1'
  );
};

const appendAmperwaveParameters = async (
  url: string,
  options: IAmperwaveOptions,
): Promise<string> => {
  const { platform, marketingDataProvider, credentialsProvider, item, featureFlags, sessionId } =
    options;

  let uri: URI = new URI(url);
  uri.addQuery({
    apv: 'a2',
    dist: 'Audacy',
    source: SourceStringsMap[platform ?? Platform.WEB],
    gpp: getGppString({ optIn: !clientServicesConfig.disableTargetedAds }),
  });

  const shouldAppendDbi = shouldOptimizeAdBreaks(featureFlags, item);

  if (shouldAppendDbi) {
    uri.addQuery('dbi', '1');
  }

  const appVersion =
    process.env.APP_VERSION ?? clientServicesConfig.personalizationServices.appVersion;

  if (appVersion) {
    uri.addQuery('a2_app_version', appVersion);
  }

  if (sessionId) {
    uri.addQuery('a2_session_id', sessionId);
  }

  const platformString = SourceStringsMap[platform];
  if (platformString) {
    uri.addQuery('source', platformString);
  }

  // good station to test this with is the station with slug 'comedy-now'
  if (uri.host().indexOf(AMPERWAVE_URL_TEST) > -1) {
    const { emailAddress, gender, zipCode, dateOfBirth, birthYear } =
      marketingDataProvider?.profile || {};

    if (emailAddress && !!featureFlags?.useHem) {
      uri.addQuery('hem', sha256(emailAddress));
    }

    const deviceId =
      platform === Platform.WEB
        ? await DataServices.getTritonCookie()
        : await (marketingDataProvider?.getAdvertisingId?.() ?? Promise.resolve(undefined));

    // mobile specific
    if (platform === Platform.ANDROID || platform === Platform.IOS) {
      uri.addQuery({
        'store-id': StoreID[platform],
        'store-url': StoreURL[platform],
        'bundle-id': BundleID[platform],
      });

      // IOS specific
      if (platform === Platform.IOS) {
        uri.addQuery(
          'atts',
          deviceId?.startsWith('idfa:')
            ? AdvertisingIdAttsType.AUTHORIZED
            : AdvertisingIdAttsType.DENIED,
        );
      }
    }

    // web specific
    if (platform === Platform.WEB) {
      syncMagnite(credentialsProvider?.userToken);

      const cookies = await syncCookies({
        enabledDSPS: ['AWZ', 'TAP', 'MAG'],
        userId: credentialsProvider?.userToken,
        gpp: getGppString({ optIn: !clientServicesConfig.disableTargetedAds }),
        amperwaveStationId: item.getId() ? parseInt(item.getId(), 10) : undefined,
        usPrivacy: item.getId(),
        tapStationId: (item as Station).getTritonName?.() || '',
        magnitePartnerKey: getRubiconUserId(),
        magniteEndpoint: 'us-east',
        timeout: 200,
      });

      if (cookies){
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cookieHandlers: any = {
          'm_partner_ids': (value: string) => value,
          'a_partner_ids': (value: string) => value,
          't_partner_ids': (value: string) => webSafe64(btoa(JSON.stringify(value))),
        };

        for (const cookieKey in cookieHandlers) {
          if (cookies[cookieKey]) {
            const transformFunction = cookieHandlers[cookieKey];
            uri.addQuery(cookieKey, transformFunction(cookies[cookieKey]));
          }
        }
      }
    }

    if (deviceId) {
      uri.addQuery('deviceid', deviceId);
    } else if (credentialsProvider?.userToken) {
      uri.addQuery('deviceid', `app:${credentialsProvider.userToken}`);
    }

    if (dateOfBirth) {
      const age = (): number | undefined =>
        dateOfBirth ? ((Date.now() - new Date(dateOfBirth).getTime()) / 3.15576e10) | 0 : undefined;
      uri.addQuery('c_age', age());
    }

    if (gender) {
      uri.addQuery('c_gender', gender);
    }

    if (zipCode) {
      uri.addQuery('c_home_zip', zipCode);
    }

    if (credentialsProvider?.userToken) {
      uri.addQuery('c_user_id', credentialsProvider.userToken);
    }

    if (birthYear) {
      uri.addQuery('c_yob', birthYear);
    }

    uri = await appendAmperwaveLocationParams(uri, options);
  }

  if (isEnableTraceSessionEnabled()) {
    uri.addQuery('ete', 'all');
  }

  return uri.normalize().valueOf();
};

export const loadSourcesForItem = async ({
  marketingDataProvider,
  locationProvider,
  credentialsProvider,
  platform,
  item,
  dataProvider: { sessionId },
}: Pick<
  IStreamerMachineContext<TPlayableObject>,
  | 'marketingDataProvider'
  | 'locationProvider'
  | 'credentialsProvider'
  | 'platform'
  | 'item'
  | 'dataProvider'
>): Promise<Array<ILiveStationAudioSource>> => {
  const sources: Array<ILiveStationAudioSource> = [];
  const featureFlags = featureFlagMap.getAll();

  if ('getHlsStream' in item && item.getHlsStream()) {
    sources.push({ url: item.getHlsStream(), type: 'm3u8' });
  }

  if ('getAacStream' in item && item.getAacStream()) {
    sources.push({ url: item.getAacStream(), type: 'aac' });
  }

  if ('getMp3Stream' in item && item.getMp3Stream()) {
    sources.push({ url: item.getMp3Stream(), type: 'mp3' });
  }

  if (!('getHlsStream' in item) && 'getAudioStream' in item && item.getAudioStream()) {
    sources.push({ url: item.getAudioStream(), type: 'm3u8' });
  }

  for (const source of sources) {
    source.url = await appendAmperwaveParameters(source.url, {
      marketingDataProvider,
      locationProvider,
      credentialsProvider,
      platform,
      item,
      sessionId,
      featureFlags,
    });
  }

  return sources;
};

export function getFirstAudioSourceUrl(
  audioSources: Record<string, string> | string | null | undefined,
): string | undefined {
  if (typeof audioSources === 'string') {
    // If audioSources is a string, return it directly
    return audioSources;
  } else if (
    audioSources &&
    typeof audioSources === 'object' &&
    Object.keys(audioSources).length > 0
  ) {
    // If audioSources is an object and not empty, return the first URL
    const firstKey = Object.keys(audioSources)[0];
    return audioSources[firstKey];
  } else {
    // If audioSources is null, undefined, or an empty object, return undefined
    return;
  }
}

export function toIntegerOrUndefined(value: number | undefined): number | undefined {
  if (typeof value === 'number') {
    return Math.floor(value);
  }
  return undefined;
}

export const loadAudioPreroll = async (tritonName: string, env: string): Promise<IAudioPreroll> => {
  try {
    const preroll = await DataServices.getAudioPreroll(tritonName, env);
    return preroll;
  } catch (e) {
    console.error('Failed to get Audio Preroll', e);
  }
  return {
    adStartTrackers: [],
    creativeUrl: '',
    companionAd: '',
    companionAdClickThroughTracker: '',
  };
};

export const sendAudioPrerollTrackers = async (trackers: Array<string>): Promise<void> => {
  try {
    DataServices.sendAudioPrerollTrackers(trackers);
  } catch (e) {
    console.error('Audio Preroll Trackers Failed', e);
  }
};

export const getExponentialBackoffDelay = (retryCount?: number) => {
  const delay = Math.min(INITIAL_RETRY_DELAY * 2 ** (retryCount || 0), MAX_DELAY);
  return delay;
};

type IMergeMetadataOptions = {
  newMetadata?: IPlayerMetadata;
  existingMetadata?: IPlayerMetadata;
  entity?: Episode | StandaloneChapter;
  item: TPlayableObject;
  logger?: ILogger;
};

export const mergeMetadata = ({
  logger,
  newMetadata,
  existingMetadata,
  entity,
  item,
}: IMergeMetadataOptions): IPlayerMetadata => {
  if (newMetadata) {
    logger?.debug('Metadata assigner got input', newMetadata);
  }

  const retVal: IPlayerMetadata = {
    ...existingMetadata,
    ...omitBy(
      entity
        ? {
            image:
              entity.getImageSquare() || entity.getParentImageSquare() || item.getImageSquare(),
            songOrShow: entity.getTitle(),
            start: entity.getStartTimeMillis(),
            stop: entity.getEndTimeMillis(),
            station: item instanceof Station ? item.getTitle() : entity.getParentTitle(),
            duration: entity.getDuration(),
          }
        : {},
      isNil,
    ),
    ...newMetadata,
  };
  // Detect new ad or content to reset companionAdIframeUrl since
  // contentType/companionAdIframeUrl only exist on the first
  // fragment of a new ad or content
  const isNewAdOrContent = Boolean(newMetadata?.contentType);

  // Make sure ad image gets deleted after ad
  // TODO: [CCS-2788] make sure this is also fixed on on Live + Exclusive stations
  if (isNewAdOrContent && !newMetadata?.companionAdIframeUrl) {
    delete retVal.companionAdIframeUrl;
  }

  if (retVal.isLive === undefined && existingMetadata) {
    retVal.isLive = existingMetadata.isLive;
  }

  if (
    (!retVal.artist && !retVal.songOrShow) ||
    // use episode metadata for talk radio content
    (!isContentTypeAd(retVal.contentType) && item.data?.category !== 'Music')
  ) {
    const showImage = entity?.data?.parentImage?.square;
    retVal.songOrShow = entity?.getTitle() ?? '';
    retVal.artist = entity?.getParentTitle() ?? '';
    retVal.image = showImage ?? retVal.image;
  }

  return retVal;
};

function getContentTitle(item: TPlayableObject): string {
  if (item?.data?.title && item.data.title.trim() !== '') {
    return item.data.title;
  } else if (item?.data?.parentShow?.title && item.data.parentShow.title.trim() !== '') {
    return item.data.parentShow.title;
  }
  return 'unknown_content_title';
}

export function reportPlaybackRequestedConviva(
  player: IPlayer,
  item: TPlayableObject,
  playLive: boolean = false,
  viewerId: string = "No viwerId present"
) {
  try {
    const contentId = item?.data?.id;
    if (!contentId) {
      return;
    }

    const contentInfoStatic: TContentInfoStatic = {
      assetName: getContentTitle(item),
      viewerId: viewerId,
      appVersion: clientServicesConfig.personalizationServices.appVersion,
      categoryType: `${item?.getEntityType()}:${item?.getEntitySubtype()}`,
      contentType: playLive ? EConvivaContentType.Live : EConvivaContentType.VOD,
      contentId: `${contentId}`,
    };
    if (player?.reportPlaybackRequested !== undefined) {
      player?.reportPlaybackRequested(contentInfoStatic);
    }
  } catch (error) {
    console.error('Error reporting playback requested to Conviva:', error);
  }
}

export function setContentInfoConviva(player: IPlayer, isLive: boolean, url: string) {
  try {
    if (player?.setContentInfo !== undefined) {
      const contentInfoDynamic = {
        isLive,
        streamUrl: url,
      };
      player?.setContentInfo(contentInfoDynamic);
    }
  } catch (error) {
    console.error('Error setting content info to Conviva:', error);
  }
}

export const TIME_ZERO: ITime = { unit: 'ms', value: 0 };
export const timeFromMs = (ms: number): ITime => ({ unit: 'ms', value: ms });
export const timeFromS = (s: number): ITime => ({ unit: 's', value: s });
export const msFromTime = (time: ITime): number =>
  time.unit === 'ms' ? time.value : time.value * 1000;
export const sFromTime = (time: ITime): number =>
  time.unit === 's' ? time.value : time.value / 1000;
export const addTimes = (time1: ITime, time2: ITime): ITime =>
  timeFromMs(msFromTime(time1) + msFromTime(time2));
export const subtractTimes = (time1: ITime, time2: ITime): ITime =>
  timeFromMs(msFromTime(time1) - msFromTime(time2));
export const minTime = (time1: ITime, time2: ITime): ITime =>
  msFromTime(time1) < msFromTime(time2) ? time1 : time2;
export const maxTime = (time1: ITime, time2: ITime): ITime =>
  msFromTime(time1) > msFromTime(time2) ? time1 : time2;
